<template>
  <div class="page">
    <!-- PAGE HEADER -->
    <div class="page-head">
      <div>
        <h1>Employees</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input v-model="query" type="text" placeholder="Search name, email..." />
        </div>

        <select v-model="roleFilter" class="select">
          <option value="">All Roles</option>
          <option v-for="r in roles" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>

        <select v-model="statusFilter" class="select">
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="DISABLED">Disabled</option>
        </select>

        <button class="btn primary" @click="openAdd()">
          <i class="bx bx-user-plus"></i>
          Add Employee
        </button>
      </div>
    </div>

    <!-- STATS ROW -->
    <div class="grid cols-4">
      <div class="card stat">
        <div class="card-title">
          <strong>Total Employees</strong>
          <span class="badge primary">LIVE</span>
        </div>
        <div class="metric">{{ employees.length }}</div>
        <div class="mini">All staff accounts</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Active</strong>
          <span class="badge success">OK</span>
        </div>
        <div class="metric">{{ activeCount }}</div>
        <div class="mini">Can access panel</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Disabled</strong>
          <span class="badge warning">BLOCK</span>
        </div>
        <div class="metric">{{ disabledCount }}</div>
        <div class="mini">Login blocked</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Last 24h Logins</strong>
          <span class="badge primary">TRACK</span>
        </div>
        <div class="metric">{{ last24h }}</div>
        <div class="mini">Recent sessions</div>
      </div>
    </div>

    <!-- TABLE (DESKTOP) -->
    <div class="card" style="margin-top: 14px;">
      <div class="table-head">
        <div class="table-title">
          <strong>Employee List</strong>
          <span class="muted">
            Showing {{ filtered.length }} records
            <span v-if="loadingList" style="margin-left: 8px;">• Loading...</span>
          </span>
        </div>

        <button class="btn ghost" @click="toast.info('Export will be added')">
          <i class="bx bx-export"></i>
          Export
        </button>
      </div>

      <!-- ✅ LOADER PLACEHOLDER -->
      <div v-if="loadingList" style="padding: 16px;">
        <div class="empty">
          <i class="bx bx-loader-circle bx-spin"></i>
          <div>
            <strong>Loading employees...</strong>
            <div class="row-muted">Please wait</div>
          </div>
        </div>
      </div>

      <div v-else class="table-wrap desktop-only">
        <table class="table">
          <thead>
            <tr>
              <th>EMPLOYEE</th>
              <th>ROLE</th>
              <th>STATUS</th>
              <th>LAST LOGIN</th>
              <th>CREATED</th>
              <th style="text-align:right;">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="emp in filtered" :key="emp.id">
              <td>
                <div class="emp">
                  <div class="avatar">{{ emp.name?.charAt(0)?.toUpperCase() }}</div>
                  <div class="emp-meta">
                    <div class="emp-name">{{ emp.name }}</div>
                    <div class="row-muted">{{ emp.email }}</div>
                  </div>
                </div>
              </td>

              <td>
                <span class="role-pill">
                  <i class="bx bx-badge-check"></i>
                  {{ roleLabel(emp.role) }}
                </span>
              </td>

              <td>
                <span class="status-pill" :class="emp.status === 'ACTIVE' ? 'success' : 'danger'">
                  <i class="bx" :class="emp.status === 'ACTIVE' ? 'bx-check-circle' : 'bx-x-circle'"></i>
                  {{ emp.status === 'ACTIVE' ? "Active" : "Disabled" }}
                </span>
              </td>

              <td class="row-muted">
                {{ emp.lastLogin || "-" }}
              </td>

              <td class="row-muted">
                {{ emp.createdAt || "-" }}
              </td>

              <td style="text-align:right;">
                <div class="row-actions">
                  <button class="icon-mini" @click="openEdit(emp)" title="Edit">
                    <i class="bx bx-edit"></i>
                  </button>

                  <button class="icon-mini" @click="openReset(emp)" title="Reset Password">
                    <i class="bx bx-key"></i>
                  </button>

                  <button
                    class="icon-mini"
                    @click="toggleStatus(emp)"
                    :title="emp.status === 'ACTIVE' ? 'Disable' : 'Enable'"
                  >
                    <i class="bx" :class="emp.status === 'ACTIVE' ? 'bx-block' : 'bx-check-shield'"></i>
                  </button>

                  <button class="icon-mini danger" @click="remove(emp)" title="Delete">
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filtered.length === 0">
              <td colspan="6" style="padding: 18px;">
                <div class="empty">
                  <i class="bx bx-user-x"></i>
                  <div>
                    <strong>No employees found</strong>
                    <div class="row-muted">Try adjusting filters or search query.</div>
                  </div>
                </div>
              </td>
            </tr>

          </tbody>
        </table>
      </div>

      <!-- MOBILE CARDS -->
      <div v-if="!loadingList" class="mobile-only list-cards">
        <div v-for="emp in filtered" :key="emp.id" class="emp-card">
          <div class="top">
            <div class="emp">
              <div class="avatar">{{ emp.name?.charAt(0)?.toUpperCase() }}</div>
              <div class="emp-meta">
                <div class="emp-name">{{ emp.name }}</div>
                <div class="row-muted">{{ emp.email }}</div>
              </div>
            </div>

            <span class="status-pill" :class="emp.status === 'ACTIVE' ? 'success' : 'danger'">
              <i class="bx" :class="emp.status === 'ACTIVE' ? 'bx-check-circle' : 'bx-x-circle'"></i>
              {{ emp.status === 'ACTIVE' ? "Active" : "Disabled" }}
            </span>
          </div>

          <div class="mid">
            <span class="role-pill">
              <i class="bx bx-badge-check"></i>
              {{ roleLabel(emp.role) }}
            </span>
            <div class="row-muted">Last Login: {{ emp.lastLogin || "-" }}</div>
          </div>

          <div class="card-actions">
            <button class="btn ghost" @click="openEdit(emp)">
              <i class="bx bx-edit"></i> Edit
            </button>
            <button class="btn ghost" @click="openReset(emp)">
              <i class="bx bx-key"></i> Reset
            </button>
            <button class="btn ghost" @click="toggleStatus(emp)">
              <i class="bx" :class="emp.status === 'ACTIVE' ? 'bx-block' : 'bx-check-shield'"></i>
              {{ emp.status === "ACTIVE" ? "Disable" : "Enable" }}
            </button>
          </div>
        </div>

        <div v-if="filtered.length === 0" class="emp-card">
          <div class="empty">
            <i class="bx bx-user-x"></i>
            <div>
              <strong>No employees found</strong>
              <div class="row-muted">Try adjusting filters or search query.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD / EDIT MODAL -->
    <div v-if="modal.open" class="modal-backdrop" @click.self="closeModal">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>{{ modal.mode === "add" ? "Add Employee" : "Edit Employee" }}</strong>
            <div class="row-muted">
              {{ modal.mode === "add" ? "Create a new staff account." : "Update employee details." }}
            </div>
          </div>
          <button class="icon-mini" @click="closeModal">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="field">
            <label>Full Name</label>
            <div class="input">
              <i class="bx bx-user"></i>
              <input v-model="form.name" placeholder="Employee name" />
            </div>
          </div>

          <div class="field">
            <label>Email</label>
            <div class="input">
              <i class="bx bx-envelope"></i>
              <input v-model="form.email" placeholder="employee@company.com" />
            </div>
          </div>

          <div class="field">
            <label>Role</label>
            <select v-model="form.role" class="select full">
              <option v-for="r in roles" :key="r.value" :value="r.value">
                {{ r.label }}
              </option>
            </select>
          </div>

          <div class="field">
            <label>Mobile (Optional)</label>
            <div class="input">
              <i class="bx bx-phone"></i>
              <input v-model="form.mobile" placeholder="9999999999" />
            </div>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeModal">Cancel</button>
          <button class="btn primary" @click="saveEmployee" :disabled="modal.saving">
            <span v-if="!modal.saving">
              <i class="bx bx-save"></i>
              Save
            </span>
            <span v-else>
              <i class="bx bx-loader-circle bx-spin"></i>
              Saving...
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- RESET PASSWORD MODAL -->
    <div v-if="reset.open" class="modal-backdrop" @click.self="closeReset">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Reset Password</strong>
            <div class="row-muted">Reset employee password to default.</div>
          </div>
          <button class="icon-mini" @click="closeReset">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="hint">
            <i class="bx bx-info-circle"></i>
            <span>
              Reset password for <b>{{ reset.emp?.name }}</b>.
              Default password will be <b>123456</b>.
            </span>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeReset">Cancel</button>
          <button class="btn primary" @click="sendReset" :disabled="reset.loading">
            <span v-if="!reset.loading">
              <i class="bx bx-send"></i>
              Reset Now
            </span>
            <span v-else>
              <i class="bx bx-loader-circle bx-spin"></i>
              Resetting...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useToast } from "vue-toastification";

import {
  getEmployees,
  addEmployee,
  updateEmployee,
  toggleEmployeeStatus,
  deleteEmployee,
  resetEmployeePassword,
} from "@/apis/empcon";

const toast = useToast();

const roles = [
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "ADMIN_MANAGER", label: "Admin Manager" },
  { value: "KYC_OFFICER", label: "KYC Officer" },
  { value: "FINANCE", label: "Finance Team" },
  { value: "SUPPORT", label: "Support Executive" },
  { value: "AUDITOR", label: "Auditor (Read Only)" },
];

const employees = ref([]);
const loadingList = ref(false);

const query = ref("");
const roleFilter = ref("");
const statusFilter = ref("");

const page_no = ref(0);
const total = ref(0);

const ldata = (() => {
  try {
    return JSON.parse(sessionStorage.getItem("ldata") || "{}");
  } catch (e) {
    return {};
  }
})();
const adminId = ldata?.user_id || "";

// UI -> DB
const statusToDb = (ui) => {
  if (!ui) return "";
  if (ui === "ACTIVE") return "active";
  if (ui === "DISABLED") return "disabled";
  return "";
};

const fetchEmployees = async () => {
  loadingList.value = true;
  try {
    const res = await getEmployees({
      page_no: page_no.value,
      keyword: query.value,
      role: roleFilter.value,
      status: statusToDb(statusFilter.value),
    });

    if (!res || res.status !== true) {
      toast.error(res?.success || "Failed to fetch employees");
      return;
    }

    const rows = res.records || [];
    employees.value = rows.map((r) => ({
      id: r.user_id,
      name: r.name || `${r.user_fname || ""} ${r.user_lname || ""}`.trim(),
      email: r.user_email || "",
      role: r.user_role || "",
      status: r.status_ui || (r.user_status === "active" ? "ACTIVE" : "DISABLED"),
      mobile: r.user_mobile || "",
      lastLogin: r.user_last_login || "-",
      createdAt: r.user_datetime || "-",
    }));

    total.value = res.total || 0;
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    loadingList.value = false;
  }
};

fetchEmployees();

// auto search
let tmr = null;
watch([query, roleFilter, statusFilter], () => {
  clearTimeout(tmr);
  tmr = setTimeout(() => {
    page_no.value = 0;
    fetchEmployees();
  }, 350);
});

const activeCount = computed(() => employees.value.filter((e) => e.status === "ACTIVE").length);
const disabledCount = computed(() => employees.value.filter((e) => e.status !== "ACTIVE").length);
const last24h = computed(() => employees.value.filter((e) => (e.lastLogin || "").includes("Today")).length);

// backend already filtered, still fallback
const filtered = computed(() => employees.value);

/* Helpers */
const roleLabel = (role) => roles.find((r) => r.value === role)?.label || role;

/* Add/Edit Modal */
const modal = reactive({
  open: false,
  mode: "add",
  emp: null,
  saving: false,
});

const form = reactive({
  name: "",
  email: "",
  role: "ADMIN_MANAGER",
  mobile: "",
});

const openAdd = () => {
  modal.open = true;
  modal.mode = "add";
  modal.emp = null;

  form.name = "";
  form.email = "";
  form.role = "ADMIN_MANAGER";
  form.mobile = "";
};

const openEdit = (emp) => {
  modal.open = true;
  modal.mode = "edit";
  modal.emp = emp;

  form.name = emp.name;
  form.email = emp.email;
  form.role = emp.role;
  form.mobile = emp.mobile || "";
};

const closeModal = () => {
  modal.open = false;
};

const saveEmployee = async () => {
  if (!form.name || !form.email) {
    toast.error("Name and email are required.");
    return;
  }

  modal.saving = true;
  try {
    if (modal.mode === "add") {
      const res = await addEmployee({
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        mobile: form.mobile.trim(),
        created_by: adminId,
      });

      if (!res || res.status !== true) {
        toast.error(res?.success || "Failed to add employee");
        return;
      }

      toast.success("Employee added ✅");
      if (res.default_password) toast.info(`Default password: ${res.default_password}`);
    } else {
      const res = await updateEmployee({
        user_id: modal.emp.id,
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        mobile: form.mobile.trim(),
        updated_by: adminId,
      });

      if (!res || res.status !== true) {
        toast.error(res?.success || "Failed to update employee");
        return;
      }

      toast.success("Employee updated ✅");
    }

    modal.open = false;
    fetchEmployees();
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    modal.saving = false;
  }
};

/* Reset Password Modal */
const reset = reactive({
  open: false,
  emp: null,
  loading: false,
});

const openReset = (emp) => {
  reset.open = true;
  reset.emp = emp;
};

const closeReset = () => {
  reset.open = false;
  reset.emp = null;
};

const sendReset = async () => {
  if (!reset.emp?.id) return;

  reset.loading = true;
  try {
    const res = await resetEmployeePassword(reset.emp.id, adminId);

    if (!res || res.status !== true) {
      toast.error(res?.success || "Failed to reset password");
      return;
    }

    toast.success("Password reset ✅");
    if (res.default_password) toast.info(`New password: ${res.default_password}`);

    closeReset();
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    reset.loading = false;
  }
};

/* Actions */
const toggleStatus = async (emp) => {
  const newUi = emp.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
  const newDb = newUi === "ACTIVE" ? "active" : "disabled";

  try {
    const res = await toggleEmployeeStatus(emp.id, newDb, adminId);

    if (!res || res.status !== true) {
      toast.error(res?.success || "Failed to update status");
      return;
    }

    toast.success(`Employee ${newUi === "ACTIVE" ? "enabled" : "disabled"} ✅`);
    fetchEmployees();
  } catch (e) {
    toast.error("Request Failed");
  }
};

const remove = async (emp) => {
  if (!confirm(`Delete employee: ${emp.name}?`)) return;

  try {
    const res = await deleteEmployee(emp.id);

    if (!res || res.status !== true) {
      toast.error(res?.success || "Failed to delete");
      return;
    }

    toast.success("Employee deleted ✅");
    fetchEmployees();
  } catch (e) {
    toast.error("Request Failed");
  }
};
</script>


<style scoped>
.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.search-mini {
  height: 44px;
  position: relative;
  min-width: 240px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px 0 42px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.select {
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.select.full {
  width: 100%;
}

.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.table-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table-title strong {
  font-weight: var(--w-bold);
}

.muted {
  color: var(--muted);
  font-size: 12px;
  font-weight: var(--w-medium);
}

.emp {
  display: flex;
  align-items: center;
  gap: 10px;
}

.emp-meta {
  display: flex;
  flex-direction: column;
}

.emp-name {
  font-weight: var(--w-bold);
  font-size: 13px;
}

.role-pill {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: var(--w-semibold);
  border: 1px solid var(--line);
  background: rgba(241, 245, 249, 0.65);
}

.role-pill i {
  font-size: 16px;
  color: rgba(100, 116, 139, 0.85);
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.icon-mini {
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
  color: rgba(11, 18, 32, 0.88);
}

.icon-mini:hover {
  transform: translateY(-1px);
}

.icon-mini.danger {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.18);
}

.icon-mini.danger i {
  color: var(--danger);
}

.empty {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 6px 2px;
}

.empty i {
  font-size: 26px;
  color: rgba(148, 163, 184, 0.9);
}

.empty strong {
  font-weight: var(--w-bold);
}

/* ---------------- Mobile cards ---------------- */
.list-cards {
  display: none;
  margin-top: 10px;
  gap: 12px;
}

.emp-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: var(--shadow-sm);
}

.emp-card .top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.emp-card .mid {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.card-actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* ---------------- Modal ---------------- */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 90;
  padding: 18px;
}

.modal {
  width: min(520px, 92vw);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
}

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.modal-head strong {
  font-weight: var(--w-bold);
  font-size: 16px;
}

.modal-body {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.modal-foot {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

@media (max-width: 960px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .list-cards {
    display: grid;
  }
}
/* ===========================================
   ✅ EMPLOYEES SCREEN MOBILE PATCH (END ONLY)
   Desktop CSS unchanged
   =========================================== */

@media (max-width: 980px) {
  /* header stack */
  .page-head {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  /* actions -> vertical */
  .actions {
    width: 100% !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  /* search input full width */
  .search-mini {
    width: 100% !important;
    min-width: 0 !important;
  }

  .search-mini input {
    width: 100% !important;
  }

  /* select full width */
  .select {
    width: 100% !important;
  }

  /* Add employee full width */
  .actions .btn.primary {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* stats -> 2 col */
  .grid.cols-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 12px !important;
  }

  /* table head stack */
  .table-head {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .table-head .btn.ghost {
    width: 100% !important;
    justify-content: center !important;
  }

  /* mobile card actions - full buttons */
  .card-actions .btn.ghost {
    flex: 1 1 100% !important;
    justify-content: center !important;
  }

  /* modal layout: keep inside screen */
  .modal-backdrop {
    padding: 14px !important;
  }

  .modal {
    width: 100% !important;
    max-width: 520px !important;
    padding: 14px !important;
    border-radius: 24px !important;
  }

  .modal-foot {
    flex-direction: column-reverse !important;
    align-items: stretch !important;
  }

  .modal-foot .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* role select spacing (inside modal) */
  .modal-body .select.full {
    width: 100% !important;
    height: 48px !important;
    border-radius: 18px !important;
  }
}

/* ✅ extra small phones */
@media (max-width: 520px) {
  .grid.cols-4 {
    grid-template-columns: 1fr !important;
  }

  .emp-card .top {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }

  .status-pill {
    width: fit-content !important;
  }
}

</style>
